import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, RefreshCw } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { DEFAVORABLE, NEUTRE_DOUX } from '../../lib/couleurs';
import {
  ETATS_DOCUMENT,
  TYPES_PIECE_LIBELLE,
  fetchDocumentPasserelle,
  retraiterDocument,
  urlFichierPasserelle,
} from '../../lib/passerelle';
import { Carte, btnLight, td, th } from '../cri/ui';

const BadgeEtat = ({ etat }) => (
  <span
    className={`border px-1.5 py-0.5 font-data text-[11px] uppercase ${
      etat === 'traite'
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
        : etat === 'exception'
          ? 'border-amber-300 bg-amber-50 text-amber-800'
          : 'border-slate-300 bg-slate-100 text-slate-600'
    }`}
  >
    {ETATS_DOCUMENT[etat] ?? etat}
  </span>
);

const Controles = ({ controles }) => (
  <ul className="space-y-1">
    {(controles ?? []).map((c) => (
      <li key={c.code} className="flex items-start gap-2 text-[11px]">
        <span
          className={`mt-0.5 border px-1 font-data uppercase ${
            c.etat === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : c.etat === 'doute'
                ? 'border-amber-300 bg-amber-50 text-amber-800'
                : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {c.etat}
        </span>
        <span className="text-slate-600">{c.message}</span>
      </li>
    ))}
  </ul>
);

const Valeur = ({ libelle, brut, normalise, page }) => (
  <div className="flex items-start justify-between gap-3 px-3 py-1.5">
    <div>
      <p className="text-[11px] text-slate-500">{libelle}</p>
      {brut != null && <p className="font-data text-[11px] text-slate-500">lu : « {String(brut)} »{page ? ` (p. ${page})` : ''}</p>}
    </div>
    <p className="font-data text-sm text-slate-900">{normalise ?? <span className="text-amber-800">Absent</span>}</p>
  </div>
);

const Detail = ({ detail, onMaj }) => {
  const { source, extraction, liens, jobs, exceptions } = detail;
  const relancer = async () => {
    const { error, data } = await retraiterDocument(source._id);
    if (error) return toast.error(error);
    toast.success(`${data.taches_remises_en_file} tâche(s) remise(s) en file — aucun effet comptable ne sera recréé.`);
    onMaj();
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <BadgeEtat etat={source.etat} />
        <span className="font-data text-xs text-slate-500">SHA-256 {source.sha256?.slice(0, 16)}…</span>
        <a data-testid="passerelle-apercu" href={urlFichierPasserelle(source._id)} target="_blank" rel="noreferrer" className={btnLight}>
          <Download size={13} /> Prévisualiser l’original archivé
        </a>
        <button data-testid="passerelle-retraiter" type="button" onClick={relancer} className={btnLight}>
          <RefreshCw size={13} /> Retraiter
        </button>
      </div>

      <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-3">
        {[
          ['Canal', source.canal],
          ['Pièces détectées', String(source.pieces ?? 0)],
          ['Origine de l’extraction', extraction?.origine === 'fixture' ? 'Fixture déterministe' : 'Lecture IA'],
        ].map(([k, v]) => (
          <div key={k} className="bg-white px-3 py-2">
            <p className="text-[11px] text-slate-500">{k}</p>
            <p className="font-data text-sm text-slate-900">{v ?? '—'}</p>
          </div>
        ))}
      </div>

      {(extraction?.pieces ?? []).map((p) => {
        const n = p.normalise;
        return (
          <div key={p.piece_index} className="border border-slate-200 bg-white">
            <p className="border-b border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
              Pièce {p.piece_index + 1} — {TYPES_PIECE_LIBELLE[n.type_piece] ?? n.type_piece}
              {p.incertain && <span className="ml-2 border border-amber-300 bg-amber-50 px-1 text-[10px] text-amber-800">Lecture incertaine</span>}
            </p>
            <div className="grid gap-px bg-slate-100 sm:grid-cols-2">
              <div className="divide-y divide-slate-100 bg-white">
                <Valeur libelle="Émetteur" brut={p.brut?.emetteur?.nom} normalise={n.emetteur?.nom} />
                <Valeur libelle="Matricule fiscal" normalise={n.emetteur?.matricule_fiscal} />
                <Valeur libelle="Numéro de pièce" brut={p.champs_bruts?.numero?.brut} page={p.champs_bruts?.numero?.page} normalise={n.numero_normalise} />
                <Valeur libelle="Date de pièce" normalise={n.date_piece} />
                <Valeur libelle="Devise" normalise={n.devise} />
              </div>
              <div className="divide-y divide-slate-100 bg-white">
                <Valeur libelle="HT" normalise={n.montants_millimes?.ht != null ? `${fmt(n.montants_millimes.ht / 1000, 2)} TND` : null} />
                <Valeur libelle="TVA" normalise={n.montants_millimes?.tva != null ? `${fmt(n.montants_millimes.tva / 1000, 2)} TND` : null} />
                <Valeur
                  libelle="Total"
                  brut={p.champs_bruts?.total?.brut}
                  page={p.champs_bruts?.total?.page}
                  normalise={n.montants_millimes?.total != null ? `${fmt(n.montants_millimes.total / 1000, 2)} TND` : null}
                />
                <Valeur libelle="Retenue à la source" normalise={n.montants_millimes?.rs_montant != null ? `${fmt(n.montants_millimes.rs_montant / 1000, 2)} TND` : null} />
                <div className="px-3 py-2">
                  <p className="mb-1 text-[11px] text-slate-500">Contrôles</p>
                  <Controles controles={p.controles} />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="border border-slate-200 bg-white">
          <p className="border-b border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
            Opérations rattachées
          </p>
          {liens.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-500">Aucun rattachement.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {liens.map((l) => (
                <div key={l._id} className="flex items-center justify-between px-3 py-2">
                  <div>
                    <p className="font-data text-sm text-slate-900">{l.objet?.numero ?? l.objet_id}</p>
                    <p className="text-[11px] text-slate-500">
                      {l.role} · {l.objet?.issuer_nom ?? l.objet_type}
                    </p>
                  </div>
                  <p className="font-data text-sm text-slate-900">{l.objet?.total_tnd != null ? `${fmt(l.objet.total_tnd, 2)} TND` : '—'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-slate-200 bg-white">
          <p className="border-b border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Traitements</p>
          <div className="divide-y divide-slate-100">
            {jobs.map((j) => (
              <div key={j._id} className="flex items-center justify-between px-3 py-2">
                <div>
                  <p className="font-data text-sm text-slate-900">{j.etape}</p>
                  <p className="text-[11px] text-slate-500">
                    {j.tentatives} tentative(s) · prochaine {String(j.prochaine_tentative).slice(0, 19).replace('T', ' ')}
                  </p>
                  {j.erreur && <p className={`text-[11px] ${DEFAVORABLE}`}>{j.erreur}</p>}
                </div>
                <span className="border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-data text-[11px] uppercase text-slate-600">{j.etat}</span>
              </div>
            ))}
          </div>
          {exceptions.length > 0 && (
            <p className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              {exceptions.length} exception(s) liée(s) à ce document — voir l’onglet Exceptions.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export const DocumentsPasserelle = ({ documents, onMaj }) => {
  const [ouvert, setOuvert] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filtre, setFiltre] = useState('tous');

  useEffect(() => {
    if (!ouvert) return setDetail(null);
    fetchDocumentPasserelle(ouvert).then(setDetail);
  }, [ouvert, documents]);

  const visibles = useMemo(
    () => (filtre === 'tous' ? documents : documents.filter((d) => d.etat === filtre)),
    [documents, filtre]
  );

  return (
    <div className="space-y-4">
      <Carte
        testid="passerelle-documents"
        titre="Documents acquis"
        sous="Original archivé avant analyse, puis extraction, identification et rattachement. Cliquez une ligne pour voir les preuves."
        action={
          <div className="flex flex-wrap gap-1">
            {['tous', 'traite', 'exception', 'recu'].map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`passerelle-filtre-${f}`}
                onClick={() => setFiltre(f)}
                className={`border px-2 py-1 text-[11px] uppercase ${filtre === f ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-600'}`}
              >
                {f === 'tous' ? 'Tous' : ETATS_DOCUMENT[f] ?? f}
              </button>
            ))}
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                <th className={th}>Fichier</th>
                <th className={th}>Canal</th>
                <th className={th}>Reçu le</th>
                <th className={th}>Pièces</th>
                <th className={th}>Rattachements</th>
                <th className={th}>État</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibles.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={6}>
                    Aucun document dans cette vue.
                  </td>
                </tr>
              )}
              {visibles.map((d) => (
                <tr
                  key={d._id}
                  data-testid={`passerelle-doc-${d._id}`}
                  onClick={() => setOuvert((p) => (p === d._id ? null : d._id))}
                  className="cursor-pointer transition-colors duration-150 hover:bg-slate-50"
                >
                  <td className={`${td} font-semibold`}>{d.nom}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{d.canal}</td>
                  <td className={td}>{String(d.cree_le).slice(0, 19).replace('T', ' ')}</td>
                  <td className={td}>{d.pieces ?? 0}</td>
                  <td className={td}>{d.liens ?? 0}</td>
                  <td className="px-3 py-2">
                    <BadgeEtat etat={d.etat} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>

      {detail && (
        <Carte testid="passerelle-detail" titre={`Preuves — ${detail.source.nom}`} sous="Valeur brute lue, valeur normalisée et page de provenance des champs critiques.">
          <Detail detail={detail} onMaj={onMaj} />
        </Carte>
      )}
    </div>
  );
};
